import { useContext, useEffect } from "react";
import SearchProvider from "../../Provider/SearchProvider";
import { useNavigate } from "react-router-dom";
import Meta from "../../Meta/Meta";
import {
  SafetyCertificateFilled,
  SafetyCertificateOutlined,
  SmileFilled,
  MedicineBoxFilled,
} from "@ant-design/icons";

const bgImageMobileSrc = `${process.env.PUBLIC_URL || ""}/images/bgimages-mobile.webp`;
const bgImageSrc = `${process.env.PUBLIC_URL || ""}/images/bgimages.webp`;

const Home = () => {
  const navigate = useNavigate();
  const { searchTable, setSearchTable } = useContext(SearchProvider);

  useEffect(() => {
    setSearchTable((prev) => ({
      ...prev,
      search: "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (event) => {
    const value = event.target.value;
    setSearchTable((prev) => ({ ...prev, search: value }));
  };

  return (
    <>
      <Meta slug="ZureSmile Clear Aligners | Certified Invisible Orthodontics in Sri Lanka" />
      <div className="relative min-h-[85vh] flex items-center bg-[#001e3b]">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden bg-[#001e3b]">
          <picture className="w-full h-full">
            <source media="(max-width: 768px)" srcSet={bgImageMobileSrc} type="image/webp" />
            <img
              src={bgImageSrc}
              alt="ZureSmile Certified Doctor Network"
              className="w-full h-full object-cover"
              fetchpriority="high"
              decoding="async"
              width="1280"
              height="547"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-darkblue/80 via-darkblue/50 to-transparent"></div>
        </div>

        <div className="container relative z-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Hero Left Text */}
            <div className="lg:col-span-6 text-white space-y-6 text-center lg:text-left">
              <span className="bg-glowgreen/20 text-glowgreen font-secondary uppercase text-sm tracking-widest px-4 py-1.5 rounded-full inline-flex items-center font-semibold border border-glowgreen/30">
                <SafetyCertificateOutlined className="mr-1.5" /> Zure Smile Aligners
              </span>
              <h1 className="font-secondary font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
                Locate a ZureSmile Certified Doctor Near You
              </h1>
              <p className="font-primary text-lg sm:text-xl text-white/90 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Transform your smile discreetly and comfortably with customized 3D clear aligners engineered for precision results.
              </p>

              <div className="flex flex-wrap gap-4 justify-center lg:justify-start pt-2">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10">
                  <SafetyCertificateFilled className="text-glowgreen text-lg" />
                  <span className="font-primary text-sm text-white">100% Biocompatible</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10">
                  <SmileFilled className="text-glowgreen text-lg" />
                  <span className="font-primary text-sm text-white">Virtually Invisible</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10">
                  <MedicineBoxFilled className="text-glowgreen text-lg" />
                  <span className="font-primary text-sm text-white">Doctor Supervised</span>
                </div>
              </div>
            </div>

            {/* Hero Right Search Box */}
            <div className="lg:col-span-6 flex justify-center lg:justify-end">
              <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-2xl border border-gray-100 max-w-lg w-full">
                <span className="text-primary font-secondary uppercase text-xs font-bold tracking-wider">
                  Find Providers
                </span>
                <h2 className="font-secondary text-darkblue font-bold text-2xl sm:text-3xl mt-1 mb-6">
                  Search Clinic / Doctor
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block font-secondary font-semibold text-sm text-[#475569] mb-2">
                      Enter City, District, or Clinic Name
                    </label>
                    <input
                      type="text"
                      value={searchTable?.search || ""}
                      placeholder="e.g. Colombo, Kandy, Galle..."
                      className="w-full rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-4 py-3 text-base text-gray-700 font-primary transition"
                      onChange={onSearch}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          navigate(`/search?search=${searchTable?.search || ""}`);
                        }
                      }}
                    />
                  </div>

                  <button
                    className="w-full bg-primary text-white font-secondary font-bold text-lg py-4 rounded-xl shadow-lg hover:bg-milkgreen hover:text-darkblue transition duration-300 transform hover:-translate-y-0.5"
                    onClick={() =>
                      navigate(`/search?search=${searchTable?.search || ""}`)
                    }
                  >
                    Find Certified Care
                  </button>

                  <div className="flex justify-between items-center pt-2 text-sm font-primary text-[#64748b]">
                    <span
                      onClick={() => navigate("/clinic")}
                      className="hover:text-primary cursor-pointer font-semibold"
                    >
                      Browse All Clinics →
                    </span>
                    <span
                      onClick={() => navigate("/doctor")}
                      className="hover:text-primary cursor-pointer font-semibold"
                    >
                      Browse Doctors →
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overview & Disclaimer */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center space-y-4">
            <h3 className="font-secondary font-bold text-3xl text-darkblue">
              The ZureSmile Commitment
            </h3>
            <p className="font-primary text-lg text-[#64748b] leading-relaxed">
              We believe that a smile is not just a reflection of good oral health, but also a powerful first impression that can leave a lasting impact. Whether you are exploring clear aligners for the first time or seeking specialized doctor-led care, ZureSmile provides a seamless, comfortable, and predictable journey to your ideal smile.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
